import { useCallback, useEffect, useRef, useState } from 'react';

/** Hover-intent timings shared by every card image preview (CardLink's, set on Articles). */
export const CARD_PREVIEW_SHOW_DELAY_MS = 50;
export const CARD_PREVIEW_HIDE_DELAY_MS = 300;

export interface CardHoverPreview {
  /** True from the moment the pointer enters (before the show delay): the cue to start loading data. */
  isHovered: boolean;
  /** True once the show delay has passed; render the tooltip on this. */
  showTooltip: boolean;
  /** Cursor position for CardImageTooltip, which applies its own offset and edge flip. */
  position: { x: number; y: number };
  /** Spread on the hover trigger. */
  triggerHandlers: {
    onMouseEnter: (e: React.MouseEvent) => void;
    onMouseMove: (e: React.MouseEvent) => void;
    onMouseLeave: () => void;
  };
  /** Pass to CardImageTooltip so moving onto the preview keeps it open. */
  tooltipHandlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  /** Show at a fixed point (keyboard focus has no cursor). */
  showAt: (position: { x: number; y: number }) => void;
  /** Start the hide countdown (blur). */
  hideSoon: () => void;
}

/**
 * The timing half of the card image preview: a short delay before showing so a
 * passing cursor does not flash it, a longer one before hiding so the pointer
 * can travel onto the preview. Extracted from CardLink so other surfaces (the
 * Finalize Trade modal's thumbnails) use the same behaviour instead of a copy.
 * Positioning (cursor offset, viewport-edge flip) stays in CardImageTooltip.
 */
export const useCardHoverPreview = ({ disabled = false }: { disabled?: boolean } = {}): CardHoverPreview => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearShow = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  };
  const clearHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const startHide = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
      setIsHovered(false);
    }, CARD_PREVIEW_HIDE_DELAY_MS);
  }, []);

  const onMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      clearHide();
      setIsHovered(true);
      setPosition({ x: e.clientX, y: e.clientY });
      showTimeoutRef.current = setTimeout(() => setShowTooltip(true), CARD_PREVIEW_SHOW_DELAY_MS);
    },
    [disabled],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || !isHovered) return;
      setPosition({ x: e.clientX, y: e.clientY });
    },
    [disabled, isHovered],
  );

  const onMouseLeave = useCallback(() => {
    if (disabled) return;
    clearShow();
    startHide();
  }, [disabled, startHide]);

  const onTooltipMouseEnter = useCallback(() => clearHide(), []);
  const onTooltipMouseLeave = useCallback(() => startHide(), [startHide]);

  const showAt = useCallback(
    (at: { x: number; y: number }) => {
      if (disabled) return;
      setIsHovered(true);
      setPosition(at);
      showTimeoutRef.current = setTimeout(() => setShowTooltip(true), CARD_PREVIEW_SHOW_DELAY_MS);
    },
    [disabled],
  );

  const hideSoon = useCallback(() => {
    if (disabled) return;
    clearShow();
    startHide();
  }, [disabled, startHide]);

  useEffect(
    () => () => {
      clearShow();
      clearHide();
    },
    [],
  );

  return {
    isHovered,
    showTooltip,
    position,
    triggerHandlers: { onMouseEnter, onMouseMove, onMouseLeave },
    tooltipHandlers: { onMouseEnter: onTooltipMouseEnter, onMouseLeave: onTooltipMouseLeave },
    showAt,
    hideSoon,
  };
};

export default useCardHoverPreview;
